import React, { useCallback, useMemo } from 'react';
import { webApplicationRunnerRegistry as registry } from './registry';
import { useParams, useSearchParams } from 'react-router-dom';
import { InterfaceResourceProvider } from '@unifyapps/carbon/no-code/components/InterfaceResourceProvider/InterfaceResourceProvider';
import { InterfaceClient, InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import AsyncViewLoader from '@unifyapps/carbon/components/AsyncView/AsyncViewLoader';
import useFetchDeployedInterfaceResources from '@unifyapps/carbon/no-code/hooks/fetchAndInvalidation/deployedEntities/data/useFetchDeployedInterfaceResources';
import { DataSourceRecordStoreState } from '@unifyapps/carbon/no-code/stores/DataSourceRecordStore';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { safeJsonParse } from '@unifyapps/carbon/utils/json';
import { EmptyStateDeprecated } from '@unifyapps/ui/components/EmptyStateDeprecated';
import { StyleOverride } from '@unifyapps/ui/styles/StyleOverride';
import { useInjectHTML } from '@unifyapps/carbon/no-code/hooks/useInjectHTML';
import userStore from '@unifyapps/carbon/auth/userStore';
import { InterfaceResourceStrategyProvider } from '@unifyapps/carbon/no-code/components/InterfaceProvider/context/InterfaceResourceStrategy';
import { DeployedResources } from '@unifyapps/carbon/no-code/components/InterfaceProvider/constants/strategies';
import { INTERFACE_THEME, UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import { InterfaceContainer } from '@unifyapps/carbon/no-code/components/InterfaceContainer';
import { UppyProvider } from '@unifyapps/carbon/components/UppyProvider';
import { QueryMeta } from '@unifyapps/network/utils/fetch/types';
import { EmitEventType, TriggerEventParams } from '@unifyapps/defs/hooks/useBlockEvents';
import FlagsProvider from '@unifyapps/carbon/context/FlagsContext';

const mode = InterfaceModes.RUNNER;
const DeltaMatrixInterfaceContainer = ({
  interfaceId,
  queryMeta,
  onClose,
  eventEmitterRef,
  onPageEvent,
}: {
  interfaceId: string;
  queryMeta: QueryMeta;
  onClose?: () => void;
  eventEmitterRef?: React.RefObject<{ emitEvent: EmitEventType } | null>;
  onPageEvent?: EmitEventType;
}) => {
  let { pageSlug } = useParams();
  let [searchParams] = useSearchParams();
  const currentUserDetails = userStore.use.currentUserDetails();
  const isLoggedIn = Boolean(currentUserDetails);
  const pageInputs = useMemo(
    () => safeJsonParse(searchParams.get('pageInputs') ?? '', {}),
    [searchParams],
  );

  const {
    data,
    dataSources,
    error,
    interfacePage,
    interfaceModulesMap,
    isLoading,
    commonNamespace,
    interfaceComponentNamespace,
  } = useFetchDeployedInterfaceResources({
    interfaceId: interfaceId,
    interfaceComponent: { slug: pageSlug ?? '', type: InterfacePageEntityComponentType.PAGE },
    // using preview call since `/auth/interface` headers are not correct in BE. will remove this once fixed
    client: InterfaceClient.UA_PLATFORM,
    mode: InterfaceModes.PREVIEW,
  });

  const emitPageEvent = useCallback(
    (data: Omit<TriggerEventParams, 'doAction'>) => {
      const { eventType, label, ...rest } = data;
      return Promise.resolve(
        onPageEvent?.({
          ...rest,
          eventType: label!,
          eventId: eventType,
        }),
      );
    },
    [onPageEvent],
  );

  useInjectHTML(data?.properties.customCode?.header, currentUserDetails, 'head');
  useInjectHTML(data?.properties.customCode?.footer, currentUserDetails, 'body');

  if (isLoading) {
    return <AsyncViewLoader />;
  }

  if (!data) {
    return <EmptyStateDeprecated size="lg" title="Interface Record not found" />;
  }

  if (!interfacePage) {
    return <EmptyStateDeprecated size="lg" title="Interface Page not found" />;
  }

  const style = data.properties.theme?.output;
  const scheme = data.properties.theme?.defaultScheme;

  return (
    <FlagsProvider isUploadAllowed={Boolean(queryMeta?.isPublicInterface) || isLoggedIn}>
      <UppyProvider
        disabledMultipartUpload={data.properties.flags?.disabledMultipartUpload}
        queryMeta={queryMeta}
      >
        <StyleOverride style={style} />
        <InterfaceResourceStrategyProvider strategy={DeployedResources.strategy}>
          <InterfaceResourceProvider
            key={pageSlug}
            dataSources={dataSources as unknown as DataSourceRecordStoreState['dataSources']}
            interfaceRecord={data}
            mode={mode}
            modules={interfaceModulesMap}
            page={interfacePage}
            registry={registry}
            pageInputs={pageInputs}
            emitPageEvent={emitPageEvent}
            interfaceComponentNamespace={interfaceComponentNamespace}
            commonNamespace={commonNamespace}
          >
            {/**
             * We may have our container with 100% viewport dimensions (especially in case of copilot) and then our copilot chat is rendered using absolute position.
             * There we set pointer-events-none class (given in ua-web-sdk), to prevent our container from blocking main page pointer events.
             * So, inside our interface page we need to enable pointer events again by giving pointer-events-auto class so that interactions work.
             */}
            <InterfaceContainer
              className={`${UA_THEME_VARS} ${INTERFACE_THEME} pointer-events-auto antialiased`}
              colorScheme={scheme}
              onClose={onClose}
              eventEmitterRef={eventEmitterRef}
            />
          </InterfaceResourceProvider>
        </InterfaceResourceStrategyProvider>
      </UppyProvider>
    </FlagsProvider>
  );
};

export default DeltaMatrixInterfaceContainer;
